import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { vi_VN } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import vi from '@angular/common/locales/vi';
import { FormsModule } from '@angular/forms';
import { IconsProviderModule } from '@app/icons-provider.module';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { KeycloakService } from 'keycloak-angular';
import { NzModalService } from 'ng-zorro-antd/modal';
import { environment } from '@appEnv/environment';
import { TableModule } from '../../projects/vss-ui/src/vss-ui/modules/table/table.module';
import { MenuModule } from '../../projects/vss-ui/src/vss-ui/modules/menu/menu.module';
import { TabGroupModule } from '../../projects/vss-ui/src/vss-ui/modules/tab-group/tab-group.module';
import { AuthInterceptor } from '../../projects/vss-ui/src/vss-ui/interceptor/auth-interceptor';
import { VssUiConfig } from "../../projects/vss-ui/src/vss-ui/vss-ui.model";
import { SetupVssUiConfig } from '@app/classes/setup-vss-ui-config';
import { VssUiConfigService } from '@app/classes/vss-ui-config-service';
import { LoadingModule } from '../../projects/vss-ui/src/vss-ui/modules/loading/loading.module';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb'
import { LayoutFullComponent } from './layout/layout-full/layout-full.component';
import { LayoutBlankComponent } from './layout/layout-blank/layout-blank.component';
import { MenuNavComponent } from './layout/menu-nav/menu-nav.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { L10nLoader } from 'angular-l10n';
import { ImageModule, UploadModule } from 'projects/vss-ui/src/public-api';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { EffectsModule } from '@ngrx/effects';
import { effectsStore } from './service/stores/effects';
import { StoreModule } from '@ngrx/store';
import { StoresModel } from './service/stores/stores';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ScrollingModule } from '@angular/cdk/scrolling'
import { SharesModule } from './modules/shares/shares.module';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzDrawerModule } from 'ng-zorro-antd/drawer'
import { ErrorInterceptor } from './classes/error.interceptor';
import { AuthLayoutComponent } from './layout/auth-layout/auth-layout.component';
import { VtSSOService } from 'projects/vss-ui/src/vss-ui/services/auth/vt-sso.service';
import { VtSsoValidateTicketComponent } from './layout/vt-sso-validate-ticket.component';
import { NzIconModule } from 'ng-zorro-antd/icon'
import { DatabaseOutline } from '@ant-design/icons-angular/icons'
import { HomeComponent } from './modules/home/component/home.component'
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts';

registerLocaleData(vi);
export function HttpLoaderFactory(http: HttpBackend) {
  return new TranslateHttpLoader(new HttpClient(http), '../assets/locales/', '.json')
}

// Initialize translation before app starts
export function initializeTranslation(translate: TranslateService): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise((resolve) => {
      const defaultLang = localStorage.getItem('language') || 'vi';
      translate.setDefaultLang(defaultLang);

      // Wait for translations to actually load
      translate.use(defaultLang).subscribe({
        next: () => {
          resolve(true);
        },
        error: (err) => {
          resolve(true); // Still resolve to allow app to load
        }
      });
    });
  };
}

function initializeKeycloak(keycloak: KeycloakService): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        await keycloak.init({
          config: {
            url: environment.keycloak.url,
            realm: environment.keycloak.realm,
            clientId: environment.keycloak.clientId,
          },
          loadUserProfileAtStartUp: false,
          initOptions: {
            onLoad: 'login-required',
            checkLoginIframe: true
          },
          bearerExcludedUrls: [
          ],
        });
        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  };
}

export function initL10n(l10nLoader: L10nLoader): Function {
  return () => l10nLoader?.load();
}

@NgModule({
  declarations: [
    AppComponent,
    LayoutFullComponent,
    LayoutBlankComponent,
    AuthLayoutComponent,
    MenuNavComponent,
    VtSsoValidateTicketComponent,
    HomeComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    IconsProviderModule,
    NzLayoutModule,
    NzMenuModule,
    MenuModule,
    TableModule,
    TabGroupModule,
    LoadingModule,
    ImageModule,
    NzAvatarModule,
    TranslateModule.forRoot({
      defaultLanguage: 'vi',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      }
    }),
    NzDropDownModule,
    NzSelectModule,
    EffectsModule.forRoot(effectsStore),
    StoreModule.forRoot(StoresModel),
    NzPopoverModule,
    NzBadgeModule,
    NzTabsModule,
    NzToolTipModule,
    ScrollingModule,
    SharesModule,
    NzBreadCrumbModule,
    UploadModule,
    NzInputModule,
    NzCardModule,
    NzEmptyModule,
    NzDrawerModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts')
    }),
  ],
  providers: [
    KeycloakService,
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeKeycloak,
    //   deps: [KeycloakService],
    //   multi: true,
    // },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslation,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (vtSSOService: VtSSOService) => () => vtSSOService.loadTicketId(),
      deps: [VtSSOService],
      multi: true
    },
    { provide: NZ_I18N, useValue: vi_VN },
    {
      provide: VssUiConfig,
      useClass: SetupVssUiConfig,
      deps: [VssUiConfigService]
    },
    // {
    //   provide: VssApiService,
    //   useClass: BaseService,
    //   deps: [HttpClient]
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    NzModalService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
